import React, { useState, useEffect } from 'react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import productAPI from '../api/productAPI';
import { jwtDecode } from 'jwt-decode'; // Make sure to install: npm install jwt-decode

const ProductSearchInput = ({ onSelect, disabled }) => {
  const { isAuthReady, isAuthenticated, isRefreshRevoked, getAccessToken } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [storeId, setStoreId] = useState(null);

  // Get store ID from token on component mount
  useEffect(() => {
    if (!isAuthReady || !isAuthenticated || isRefreshRevoked) {
      setStoreId(null);
      return;
    }

    const token = getAccessToken();
    if (!token) return;
    if (token) {
      try {
        const decoded = jwtDecode(token);
        // Assuming stores array is in the token, take the first store
        const userStoreId = decoded.stores?.[0]?._id || decoded.stores?.[0];
        setStoreId(userStoreId);
      } catch (error) {
        console.error('Error decoding token:', error);
      }
    }
  }, [isAuthReady, isAuthenticated, isRefreshRevoked, getAccessToken]);

  useEffect(() => {
    const searchProducts = async () => {
      if (!isAuthReady || !isAuthenticated || isRefreshRevoked || !getAccessToken()) {
        setResults([]);
        setShowDropdown(false);
        return;
      }

      if (searchTerm.trim().length < 2 || !storeId) {
        setResults([]);
        setShowDropdown(false);
        return;
      }

      setIsSearching(true);
      try {
        const response = await productAPI.search(searchTerm);
        setResults(response.data || []);
        setShowDropdown(response.data?.length > 0);
      } catch (error) {
        console.error('Search error:', error);
        setResults([]);
        setShowDropdown(false);
      } finally {
        setIsSearching(false);
      }
    };

    const debounceTimer = setTimeout(() => {
      searchProducts();
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm, storeId, isAuthReady, isAuthenticated, isRefreshRevoked, getAccessToken]);

  const handleSelect = (product) => {
    onSelect(product);
    setSearchTerm('');
    setResults([]);
    setShowDropdown(false);
  };

  const handleClear = () => {
    setSearchTerm('');
    setResults([]);
    setShowDropdown(false);
  };

  return (
    <div className="relative">
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => results.length > 0 && setShowDropdown(true)}
          onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
          className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 sm:text-sm"
          placeholder="Search by name, barcode, or SKU..."
          disabled={disabled || !storeId || !isAuthReady || !isAuthenticated || isRefreshRevoked}
        />
        {searchTerm && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute inset-y-0 right-0 pr-3 flex items-center"
            disabled={disabled}
          >
            <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
          </button>
        )}
      </div>

      {showDropdown && (
        <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm max-h-60">
          {isSearching ? (
            <div className="px-4 py-2 text-gray-500">Searching...</div>
          ) : results.length === 0 ? (
            <div className="px-4 py-2 text-gray-500">
              {!storeId 
                ? 'No store selected' 
                : searchTerm.trim().length < 2 
                  ? 'Enter at least 2 characters' 
                  : 'No matching products found'}
            </div>
          ) : (
            results.map((product) => (
              <div
                key={product._id}
                className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex justify-between items-center"
                onMouseDown={() => handleSelect(product)}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{product.name}</div>
                  <div className="text-sm text-gray-500 truncate">
                    {product.barcode && <span>Barcode: {product.barcode} • </span>}
                    {product.sku && <span>SKU: {product.sku} • </span>}
                    {product.uom || 'each'} • {product.cost?.toFixed(2) || '0.00'}
                  </div>
                </div>
                <div className="ml-2 flex-shrink-0">
                  <span className="text-xs bg-teal-100 text-teal-800 px-2 py-1 rounded">
                    Add
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default ProductSearchInput;